import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Dialog from "@mui/material/Dialog";
import type Plyr from "plyr";

import { localSrcsetFor } from "@/components/marketing/localSrcset";
import { NOT_WIDE, TINY } from "@/components/marketing/tokens";
import { captureException } from "@/observability/sentry";
import { HOME_HERO } from "./homeContent";

/**
 * The published "See How it Works" modal: a `modal_1` frame around a Plyr
 * player, with a cover the reader clicks to start. Plyr is the player the
 * published page uses, at its version, configured and skinned as that page
 * configures and skins it, and loaded only once the modal first opens.
 *
 * `src` is the CMS's. With none the cover still draws and the control is
 * inert, which is what the published page shows today: its own source has
 * expired and its player goes black.
 */

/** The published `new Plyr(...)` options, verbatim. */
const PLYR_OPTIONS = {
  controls: ["play", "progress", "current-time", "mute", "fullscreen"],
  resetOnEnd: true,
};

/** The published page's overrides of Plyr's variables and rules, verbatim. */
const PLYR_SKIN = {
  "--plyr-color-main": "white",
  "--plyr-tab-focus-color": "transparent",
  "--plyr-video-control-color-hover": "black",
  "--plyr-control-icon-size": "1.5em",
  "--plyr-range-thumb-height": "0px",
  "--plyr-range-track-height": "0.6em",
  "& .plyr__control--overlaid": { color: "black" },
  "& .plyr__volume": { width: "auto", minWidth: "auto", maxWidth: "auto" },
  "& .plyr": { width: "100%", height: "100%" },
  "& .plyr video": { objectFit: "cover" },
};

const CLOSE_LABEL = "Close";
/** `.modal_1_backdrop` */
const BACKDROP = "rgba(0, 0, 0, 0.35)";
/** `.video-shadow_box` */
const BOX_SHADOW = "rgba(38, 27, 77, 0.67) 0px 4px 20px -9px";
/** `.hero-video-new-wrap`: the published box is 16:10, stated as its padding. */
const ASPECT = "62.52%";
const RADIUS = { wide: "32px", tablet: "22.4px", tiny: "8px" };
const COVER_BUTTON = { wide: 96, tablet: 77, tiny: 67 };
/** How long the cover takes to go, and its image to grow, once the video plays. */
const COVER_FADE = "0.4s";

/** `.modal_1_content`: inset 16px, capped at the published page's own width. */
const FRAME = {
  width: "calc(100vw - 32px)",
  maxWidth: 1440,
  maxHeight: "calc(100% - 32px)",
  m: "16px",
  borderRadius: "16px",
  backgroundColor: "transparent",
  boxShadow: "none",
  overflow: "hidden auto",
  textAlign: "center",
};

/** `.modal_1_close`: a pill above the box, in the page's own type. */
const CLOSE = {
  // The frame is a flex column, which would otherwise stretch the pill across it.
  alignSelf: "center",
  mb: "8px",
  p: "16px 24px",
  borderRadius: "100vw",
  backgroundColor: "common.black",
  color: "common.white",
  fontFamily: "inherit",
  fontSize: 16,
  lineHeight: "24px",
  [TINY]: { p: "11.2px 16px" },
};

const BOX = {
  position: "relative",
  // The published box keeps its aspect and the frame scrolls; as a flex item
  // this one would shrink to fit a short viewport instead.
  flexShrink: 0,
  overflow: "hidden",
  borderRadius: RADIUS.wide,
  boxShadow: BOX_SHADOW,
  [NOT_WIDE]: { borderRadius: RADIUS.tablet },
  [TINY]: { borderRadius: RADIUS.tiny },
};

const STAGE = {
  position: "absolute",
  inset: 0,
  backgroundColor: "common.black",
};

const COVER = {
  position: "absolute",
  inset: 0,
  zIndex: 2,
  cursor: "pointer",
  transition: `opacity ${COVER_FADE}`,
  "&[data-hidden]": { opacity: 0, pointerEvents: "none" },
  "&[data-hidden] img": { transform: "scale(1.4)" },
};

const COVER_IMAGE = {
  display: "block",
  width: 1,
  height: 1,
  objectFit: "cover",
  transition: `transform ${COVER_FADE}`,
};

const COVER_PLAY = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: COVER_BUTTON.wide,
  height: COVER_BUTTON.wide,
  borderRadius: "100vw",
  backgroundColor: "common.white",
  color: "common.black",
  [NOT_WIDE]: { width: COVER_BUTTON.tablet, height: COVER_BUTTON.tablet },
  [TINY]: { width: COVER_BUTTON.tiny, height: COVER_BUTTON.tiny },
};

/** The published cover's glyph, inlined: a triangle in a 110-unit box, at 40% of the button. */
function PlayGlyph() {
  return (
    <Box
      component="svg"
      viewBox="0 0 110 110"
      aria-hidden
      sx={{ width: "40%", height: "40%" }}
    >
      <path
        d="M55,0l55,77H0Z"
        transform="translate(100 0) rotate(90)"
        fill="currentColor"
      />
    </Box>
  );
}

/**
 * The player, mounted with the modal and gone with it, so every open starts
 * from the cover as the published one does. Plyr and its stylesheet arrive on
 * the first mount; a failure to arrive costs the controls, not the modal.
 */
function Player({ src }: { src: string }) {
  const video = useRef<HTMLVideoElement>(null);
  const player = useRef<Plyr | null>(null);
  const [covered, setCovered] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ default: PlyrCtor }] = await Promise.all([
          import("plyr"),
          import("plyr/dist/plyr.css"),
        ]);
        if (cancelled || !video.current) {
          return;
        }
        const instance = new PlyrCtor(video.current, PLYR_OPTIONS);
        // The cover leaves as the video starts and returns once it has ended,
        // where `resetOnEnd` has already wound it back to the start.
        instance.on("play", () => setCovered(false));
        instance.on("ended", () => setCovered(true));
        player.current = instance;
      } catch (error) {
        captureException(error);
      }
    })();
    return () => {
      cancelled = true;
      player.current?.destroy();
      player.current = null;
    };
  }, []);

  return (
    <Box sx={[STAGE, PLYR_SKIN]}>
      <video
        ref={video}
        playsInline
        controls
        style={{ width: "100%", height: "100%" }}
      >
        <source src={src} type="video/mp4" />
      </video>
      <Cover covered={covered} onPlay={() => player.current?.play()} />
    </Box>
  );
}

function Cover({ covered, onPlay }: { covered: boolean; onPlay?: () => void }) {
  return (
    <Box
      role="button"
      aria-label="Play"
      data-hidden={covered ? undefined : ""}
      onClick={onPlay}
      sx={COVER}
    >
      <Box
        component="img"
        src={HOME_HERO.poster}
        srcSet={localSrcsetFor(HOME_HERO.poster, HOME_HERO.posterSrcsetWidth)}
        sizes="100vw"
        alt=""
        sx={COVER_IMAGE}
      />
      <Box sx={COVER_PLAY}>
        <PlayGlyph />
      </Box>
    </Box>
  );
}

export default function HeroVideoDialog({
  open,
  onClose,
  src,
}: {
  open: boolean;
  onClose: () => void;
  src: string | null;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-label={HOME_HERO.videoLabel}
      slotProps={{
        paper: { sx: FRAME },
        backdrop: { sx: { backgroundColor: BACKDROP } },
      }}
    >
      <ButtonBase type="button" onClick={onClose} sx={CLOSE}>
        {CLOSE_LABEL}
      </ButtonBase>
      <Box sx={BOX}>
        <Box sx={{ pt: ASPECT }} />
        {src ? (
          <Player src={src} />
        ) : (
          <Box sx={STAGE}>
            <Cover covered />
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
